"use client";

import { useState } from "react";
import Image from "next/image";
import SectionTitle from "@/components/ui/SectionTitle";

interface Industry {
  name: string;
  iconSrc: string;
  description: string;
  url?: string;
}

const industries: Industry[] = [
  { name: "Local services", iconSrc: "/icons/specialpage/IndustriesWeServed (8).svg", description: "Generate more local inquiries with high-intent Google Ads, conversion-focused landing pages, and performance marketing campaigns tailored to your service area. - local service" },
  { name: "eCommerce & DTC", iconSrc: "/icons/specialpage/IndustriesWeServed (4).svg", description: "Google Ads management for eCommerce, Meta ads for D2C brands, Shopping feeds, dynamic retargeting, and ROAS optimization for direct-to-consumer brands scaling in the U.S.",url: "https://www.makemybrandlabs.com/industries/e-commerce" },
  { name: "B2B SaaS & Tech", iconSrc: "/icons/specialpage/IndustriesWeServed (3).svg", description: "Google Ads agency for SaaS, paid search, account-based demand capture, demo funnels, and trial-to-paid optimization built around CAC, LTV, pipeline velocity, and lead quality." },
  { name: "Healthcare", iconSrc: "/icons/specialpage/IndustriesWeServed (2).svg", description: "HIPAA-compliant patient acquisition funnels, local Google Ads, and compliant Meta campaigns for clinics and health platforms.",url: "https://www.makemybrandlabs.com/industries/healthcare" },
  { name: "Real Estate", iconSrc: "/icons/specialpage/IndustriesWeServed (1).svg", description: "High-intent Google Ads, geo-targeted Meta campaigns, and lead funnels for agents, brokerages, and proptech companies.",url:"https://www.makemybrandlabs.com/industries/real-estate" },
  { name: "Education & EdTech", iconSrc: "/icons/specialpage/IndustriesWeServed (6).svg", description: "Enrollment campaigns, course launch strategies, and lead generation agency support for B2B and B2C learning platforms.", url: "https://www.makemybrandlabs.com/industries/ed-tech" },
  { name: 'Legal Services', iconSrc: "/icons/specialpage/IndustriesWeServed (5).svg", description: "Compliant, high-converting campaigns for law firms. We optimize for cost-per-case across personal injury, family law, and immigration." },
  { name: "Startups", iconSrc: "/icons/specialpage/IndustriesWeServed (7).svg", description: "Performance marketing agency for startups that need to validate CAC, test paid channels, build repeatable demand, and hit growth milestones without burning runway",  },
];

export default function IndustriesWeServe() {
  const [active, setActive] = useState(0);

  const CardContent = ({ item, isActive }: { item: Industry; isActive: boolean }) => (
    <>
      <Image
        src={item.iconSrc}
        alt={item.name}
        width={40}
        height={40}
        className={`transition-all duration-300 ${isActive ? "scale-110" : ""}`}
      />
      <span className={`mt-3 font-semibold leading-tight ${isActive ? "text-red-500" : "text-gray-700"}`}>
        {item.name}
      </span>
      {isActive && (
        <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
          <span className="relative flex h-4 w-4">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-red-500 opacity-30" />
            <span className="relative inline-flex h-4 w-4 rounded-full bg-red-500 border-2 border-white" />
          </span>
        </div>
      )}
    </>
  );

  return (
    <section className="py-20 section-bg-white-soft">
      <div className="container mx-auto px-4 text-center flex flex-col gap-12">
        <SectionTitle
          mobileBadge="Specialized Performance Marketing for U.S. Markets"
          desktopBadge="Specialized Performance Marketing for U.S. Growth Markets"
          title="Industries We Serve"
          description="Every industry has different buyer intent, sales cycles, compliance needs, and conversion benchmarks..."
          align="center"
        />

        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-4 max-w-7xl mx-auto">
          {industries.map((item, index) => {
            const isActive = active === index;

            if (item.url) {
              // Link card
              return (
                <a
                  key={item.name}
                  href={item.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  onMouseEnter={() => setActive(index)}
                  className={`relative overflow-visible flex flex-col items-center justify-center p-6 border transition-all duration-300 hover:border-gray-300 hover:shadow-lg ${
                    isActive ? "border-red-500 bg-white shadow-lg" : "border-gray-200 bg-white"
                  } cursor-pointer`}
                >
                  <CardContent item={item} isActive={isActive} />
                </a>
              );
            } else {
              // Button card
              return (
                <button
                  key={item.name}
                  onClick={() => setActive(index)}
                  className={`relative overflow-visible flex flex-col items-center justify-center p-6 border transition-all duration-300 hover:border-gray-300 hover:shadow-lg ${
                    isActive ? "border-red-500 bg-white shadow-lg" : "border-gray-200 bg-white"
                  } cursor-pointer`}
                >
                  <CardContent item={item} isActive={isActive} />
                </button>
              );
            }
          })}
        </div>

        <div>
          <h3 className="text-2xl font-bold text-gray-900 mb-2">{industries[active].name}</h3>
          <p className="max-w-3xl mx-auto text-gray-600 leading-relaxed">
            {industries[active].description}
          </p>
        </div>
      </div>
    </section>
  );
}